// ========================================
// API SERVICE - Centralized Axios Instance
// All API calls go through here
// Updated with Unit Types, Units, Plans, Amenities
// ========================================

import axios from 'axios';
import { sessionManager } from './sessionManager';

// Resolve baseURL — when running on a custom-subdomain host (e.g.
// hiranandaniwestgate.archiverse.ai) the configured REACT_APP_API_URL
// (https://app.archiverse.ai) would be cross-origin and the backend
// CORS guard would 503 us. Switch to relative URLs in that case so
// API calls hit the same origin that's serving the page; nginx's
// wildcard server block proxies /api/* to the backend regardless.
function resolveBaseURL() {
  const configured = process.env.REACT_APP_API_URL || '';
  if (!configured) return '';
  if (typeof window === 'undefined') return configured;
  try {
    const configuredOrigin = new URL(configured).origin;
    if (configuredOrigin === window.location.origin) return configured;
    return '';  // cross-origin → use relative
  } catch {
    return configured;
  }
}

// Create axios instance with defaults
const api = axios.create({
  baseURL: resolveBaseURL(),
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// ========================================
// REQUEST INTERCEPTOR
// Add auth token to all requests
// ========================================
api.interceptors.request.use(
  (config) => {
    const token = sessionManager.getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// ========================================
// RESPONSE INTERCEPTOR
// Handle errors globally, refresh tokens
// ========================================
api.interceptors.response.use(
  (response) => {
    // Return data directly for cleaner usage
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Handle 401 - try token refresh
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      if (sessionManager.getRefreshToken()) {
        try {
          const token = await sessionManager.refresh();
          api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
          originalRequest.headers['Authorization'] = `Bearer ${token}`;

          return api(originalRequest);
        } catch (refreshError) {
          // Refresh failed - clear auth and redirect
          sessionManager.clearSession();
          window.location.href = '/login';
          return Promise.reject(refreshError);
        }
      } else {
        // No refresh token - redirect to login
        sessionManager.clearSession();
        window.location.href = '/login';
      }
    }

    // Extract error message for easier handling
    const message = 
      error.response?.data?.error?.message ||
      error.response?.data?.error ||
      error.response?.data?.message ||
      error.message ||
      'An unexpected error occurred';

    // Attach clean message to error
    error.userMessage = message;

    return Promise.reject(error);
  }
);

// ========================================
// API ENDPOINTS
// Organized by resource
// ========================================

// Auth
export const authAPI = {
  login: (email, password) => api.post('/api/auth/login', { email, password }),
  logout: () => api.post('/api/auth/logout'),
  forgotPassword: (email) => api.post('/api/auth/forgot-password', { email }),
  resetPassword: (token, password) => api.post('/api/auth/reset-password', { token, password }),
  changePassword: (current_password, new_password) => 
    api.post('/api/auth/change-password', { current_password, new_password }),
};

// Assets
export const assetsAPI = {
  getAll: () => api.get('/api/assets'),
  getById: (id) => api.get(`/api/assets/${id}`),
  create: (data) => api.post('/api/assets', data),
  update: (id, data) => api.put(`/api/assets/${id}`, data),
  delete: (id) => api.delete(`/api/assets/${id}`),
};

// Clients
export const clientsAPI = {
  getAll: (params) => api.get('/api/clients', { params }),
  getById: (id) => api.get(`/api/clients/${id}`),
  create: (data) => api.post('/api/clients', data),
  update: (id, data) => api.put(`/api/clients/${id}`, data),
  delete: (id) => api.delete(`/api/clients/${id}`),
  bulkDelete: (ids) => api.post('/api/clients/bulk-delete', { client_ids: ids }),
  getPipelineStats: () => api.get('/api/clients/pipeline-stats'),
  addProperty: (clientId, assetId) => 
    api.post(`/api/clients/${clientId}/properties`, { asset_id: assetId }),
  removeProperty: (clientId, assetId) =>
    api.delete(`/api/clients/${clientId}/properties/${assetId}`),
};

// PLRs
export const plrAPI = {
  getAll: () => api.get('/api/plr'),
  getById: (id) => api.get(`/api/plr/${id}/edit`),
  create: (data) => api.post('/api/plr', data),
  update: (id, data) => api.put(`/api/plr/${id}`, data),
  delete: (id) => api.delete(`/api/plr/${id}`),
  getLeads: (id) => api.get(`/api/plr/${id}/leads`),
  // Sections
  getSections: (plrId) => api.get(`/api/plr/${plrId}/sections`),
  createSection: (plrId, data) => api.post(`/api/plr/${plrId}/sections`, data),
  updateSection: (plrId, sectionId, data) => 
    api.put(`/api/plr/${plrId}/sections/${sectionId}`, data),
  deleteSection: (plrId, sectionId) => 
    api.delete(`/api/plr/${plrId}/sections/${sectionId}`),
  reorderSections: (plrId, sectionIds) => 
    api.post(`/api/plr/${plrId}/sections/reorder`, { sectionIds }),
};

// Regions
export const regionsAPI = {
  getAll: () => api.get('/api/regions'),
  getById: (id) => api.get(`/api/regions/${id}`),
  create: (data) => api.post('/api/regions', data),
  update: (id, data) => api.put(`/api/regions/${id}`, data),
  delete: (id) => api.delete(`/api/regions/${id}`),
};

// Spaces (Office/Warehousing)
export const spacesAPI = {
  getByBuilding: (buildingId) => api.get(`/api/spaces/building/${buildingId}`),
  getById: (id) => api.get(`/api/spaces/${id}`),
  create: (data) => api.post('/api/spaces', data),
  update: (id, data) => api.put(`/api/spaces/${id}`, data),
  delete: (id) => api.delete(`/api/spaces/${id}`),
  bulkCreate: (spaces) => api.post('/api/spaces/bulk', { spaces }),
};

// ========================================
// NEW: Unit Types (Residential)
// ========================================
export const unitTypesAPI = {
  getByAsset: (assetId) => api.get(`/api/unit-types/asset/${assetId}`),
  getById: (id) => api.get(`/api/unit-types/${id}`),
  create: (data) => api.post('/api/unit-types', data),
  update: (id, data) => api.put(`/api/unit-types/${id}`, data),
  delete: (id) => api.delete(`/api/unit-types/${id}`),
  // Premiums
  getPremiums: (unitTypeId) => api.get(`/api/unit-types/${unitTypeId}/premiums`),
  addPremium: (unitTypeId, data) => api.post(`/api/unit-types/${unitTypeId}/premiums`, data),
  updatePremium: (unitTypeId, premiumId, data) => 
    api.put(`/api/unit-types/${unitTypeId}/premiums/${premiumId}`, data),
  deletePremium: (unitTypeId, premiumId) => 
    api.delete(`/api/unit-types/${unitTypeId}/premiums/${premiumId}`),
};

// ========================================
// NEW: Units (Residential Individual Units)
// ========================================
export const unitsAPI = {
  getByAsset: (assetId, params) => api.get(`/api/units/asset/${assetId}`, { params }),
  getByBuilding: (buildingId) => api.get(`/api/units/building/${buildingId}`),
  getById: (id) => api.get(`/api/units/${id}`),
  create: (data) => api.post('/api/units', data),
  update: (id, data) => api.put(`/api/units/${id}`, data),
  delete: (id) => api.delete(`/api/units/${id}`),
  bulkCreate: (units) => api.post('/api/units/bulk', { units }),
  // Status update
  updateStatus: (id, status) => api.patch(`/api/units/${id}/status`, { status }),
  // CSV Import
  importPreview: (assetId, formData) => 
    api.post(`/api/units/import/preview?asset_id=${assetId}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  importExecute: (assetId, data) => 
    api.post(`/api/units/import/execute?asset_id=${assetId}`, data),
  // Availability matrix
  getAvailabilityMatrix: (assetId) => api.get(`/api/units/asset/${assetId}/matrix`),
};

// ========================================
// NEW: Plans & Limits
// ========================================
export const plansAPI = {
  // Get all available plans
  getAll: () => api.get('/api/plans'),
  // Get current tenant's plan and usage
  getCurrent: () => api.get('/api/plans/current'),
  // Get specific plan details
  getById: (id) => api.get(`/api/plans/${id}`),
  // Upgrade/change plan (admin only)
  changePlan: (planId) => api.post('/api/plans/change', { plan_id: planId }),
  // Check specific limit
  checkLimit: (resourceType) => api.get(`/api/plans/check-limit/${resourceType}`),
  // Get usage stats
  getUsage: () => api.get('/api/plans/usage'),
};

// ========================================
// NEW: Amenities (Residential)
// ========================================
export const amenitiesAPI = {
  // Get all amenities (master list from tenant route)
  getAll: (category) => api.get('/api/tenant/amenities', { params: { category } }),
  // Get amenities for an asset
  getByAsset: (assetId) => api.get(`/api/assets/${assetId}/amenities`),
  // Update asset amenities
  updateAssetAmenities: (assetId, amenityIds) => 
    api.put(`/api/assets/${assetId}/amenities`, { amenity_ids: amenityIds }),
};

// Users
export const usersAPI = {
  getAll: () => api.get('/api/users'),
  create: (data) => api.post('/api/users', data),
  update: (id, data) => api.put(`/api/users/${id}`, data),
};

// Email Campaigns
export const emailAPI = {
  getAll: () => api.get('/api/email'),
  getById: (id) => api.get(`/api/email/${id}`),
  create: (data) => api.post('/api/email', data),
  update: (id, data) => api.put(`/api/email/${id}`, data),
  delete: (id) => api.delete(`/api/email/${id}`),
  send: (id) => api.post(`/api/email/${id}/send`),
};

// Gallery / Images
export const galleryAPI = {
  uploadAssetImages: (assetId, formData) => 
    api.post(`/api/gallery/asset/${assetId}/gallery`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  uploadSpaceImages: (spaceId, formData) => 
    api.post(`/api/gallery/space/${spaceId}/images`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  deleteAssetImage: (assetId, galleryId) => 
    api.delete(`/api/gallery/asset/${assetId}/gallery/${galleryId}`),
  deleteSpaceImage: (spaceId, imageId) => 
    api.delete(`/api/gallery/space/${spaceId}/images/${imageId}`),
  // Unit images (residential)
  uploadUnitImages: (unitId, formData) => 
    api.post(`/api/gallery/unit/${unitId}/images`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  deleteUnitImage: (unitId, imageId) => 
    api.delete(`/api/gallery/unit/${unitId}/images/${imageId}`),
};

// CSV Import
export const importAPI = {
  previewClients: (formData) => 
    api.post('/api/csv/preview', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  importClients: (data) => api.post('/api/csv/import', data),
  getHistory: () => api.get('/api/csv/history'),
  previewSpaces: (formData) => 
    api.post('/api/spaces-import/parse', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  importSpaces: (data) => api.post('/api/spaces-import/import', data),
};

// Client Assignments
export const assignmentsAPI = {
  get: (clientId) => api.get(`/api/client-assignments/${clientId}/assignments`),
  getProjectStructure: (clientId, assetId) => 
    api.get(`/api/client-assignments/${clientId}/project/${assetId}/structure`),
  update: (clientId, data) => 
    api.post(`/api/client-assignments/${clientId}/assignments`, data),
};

// Tenant
export const tenantAPI = {
  getBranding: () => api.get('/api/tenant/branding'),
  updateBranding: (data) => api.put('/api/tenant/branding', data),
  uploadLogo: (formData) =>
    api.post('/api/tenant/logo', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  uploadFavicon: (formData) =>
    api.post('/api/tenant/favicon', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
};

// ========================================
// Agent Chat (public - no auth for widget)
// ========================================
export const agentChatAPI = {
  getConfig: (assetId) => api.get(`/api/agent/chat/config/${assetId}`),
  sendMessage: (data) => api.post('/api/agent/chat/message', data),
  endConversation: (conversationId) => api.post('/api/agent/chat/end', { conversationId }),
  submitLead: (data) => api.post('/api/agent/chat/lead', data),
  escalate: (conversationId, reason) => api.post('/api/agent/chat/escalate', { conversationId, reason }),
  trackContentView: (data) => api.post('/api/agent/chat/content-view', data),
  // Authenticated
  getConversations: (params) => api.get('/api/agent/chat/conversations', { params }),
  getConversation: (id) => api.get(`/api/agent/chat/conversations/${id}`),
};

// Agent Knowledge Base (authenticated)
export const agentKBAPI = {
  // Roles
  getRoles: (assetId) => api.get(`/api/agent/kb/roles/${assetId}`),
  createRole: (data) => api.post('/api/agent/kb/roles', data),
  deleteRole: (roleId) => api.delete(`/api/agent/kb/roles/${roleId}`),
  // Documents
  getDocuments: (assetId) => api.get(`/api/agent/kb/documents/${assetId}`),
  uploadDocument: (formData) => api.post('/api/agent/kb/documents', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  extractQA: (documentId) => api.post(`/api/agent/kb/documents/${documentId}/extract`),
  // Q&A Pairs
  getQAPairs: (assetId, params) => api.get(`/api/agent/kb/qa/${assetId}`, { params }),
  createQA: (data) => api.post('/api/agent/kb/qa', data),
  updateQA: (qaId, data) => api.put(`/api/agent/kb/qa/${qaId}`, data),
  deleteQA: (qaId) => api.delete(`/api/agent/kb/qa/${qaId}`),
  reviewQA: (qaId, data) => api.post(`/api/agent/kb/qa/${qaId}/review`, data),
  verifyQA: (qaId, data) => api.post(`/api/agent/kb/qa/${qaId}/verify`, data),
  bulkApprove: (assetId) => api.post(`/api/agent/kb/qa/bulk-approve/${assetId}`),
  generateStandard: (assetId) => api.post(`/api/agent/kb/generate-standard/${assetId}`),
  // Verifiers
  getVerifiers: (assetId) => api.get(`/api/agent/kb/verifiers/${assetId}`),
  assignVerifier: (data) => api.post('/api/agent/kb/verifiers', data),
  removeVerifier: (assetId, userId) => api.delete(`/api/agent/kb/verifiers/${assetId}/${userId}`),
  // Stats
  getStats: (assetId) => api.get(`/api/agent/kb/stats/${assetId}`),
};

// Agent Config (admin only)
export const agentConfigAPI = {
  get: (assetId) => api.get(`/api/agent/config/${assetId}`),
  update: (assetId, data) => api.put(`/api/agent/config/${assetId}`, data),
  healthCheck: () => api.get('/api/agent/config/health/check'),
};

// ========================================
// Virtual Tours (authenticated)
// ========================================
export const virtualToursAPI = {
  getByAsset: (assetId) => api.get(`/api/virtual-tours/${assetId}`),
  create: (assetId, data) => api.post(`/api/virtual-tours/${assetId}`, data),
  update: (tourId, data) => api.put(`/api/virtual-tours/${tourId}`, data),
  delete: (tourId) => api.delete(`/api/virtual-tours/${tourId}`),
  toggleActive: (tourId, isActive) => api.put(`/api/virtual-tours/${tourId}`, { is_active: isActive }),
  // Frames
  uploadFrame: (tourId, formData) => api.post(`/api/virtual-tours/${tourId}/frames`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  deleteFrame: (tourId, frameId) => api.delete(`/api/virtual-tours/${tourId}/frames/${frameId}`),
  reorderFrames: (tourId, frameIds) => api.put(`/api/virtual-tours/${tourId}/frames/reorder`, { frameIds }),
  // Public (no auth)
  getPublic: (assetId) => api.get(`/api/virtual-tours/public/${assetId}`),
};

export default api;
